import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import messages from '../../statics/messages.json';
import ReceiveContext from '../../context/ReceiveContext';
import OrderContext from '../../context/OrderContext';
import { saveLineStatus } from '../../context/CommonContex';
import ColumnVisibilityModal from '../Common/ColumnVisibilityModal';
import ColumnGroupsModal from '../Common/ColumnGroupsModal';
import CombinedColumnCell from '../Orders/CombinedColumnCell';
import '../Orders/OrderValidation.css';
import './ProductionOrders.css';

// Same tags and order as Orders, Order Validation, and Quotes.
const LINE_STATUS_TAGS = [
    { label: 'Released', color: '#02f77c' },
    { label: 'Shipped', color: '#2D895F' },
    { label: 'In Kitting', color: '#CB5722' },
    { label: 'In Process', color: '#9288FF' },
    { label: 'In Testing', color: '#84E6F8' },
    { label: 'In Outgoing Inspection', color: '#B8B2E1' },
    { label: 'Not Released', color: '#F3DC6B' },
    { label: 'On Hold Cart', color: '#C56A00' },
    { label: 'Staging Cart', color: '#1DA1DB' },
    { label: 'Waiting On Components', color: '#A40202' },
    { label: 'PMA', color: '#594E9F' },
    { label: 'RMA', color: '#EB4F4F' },
    { label: 'ENG Review', color: '#625C5C' },
    { label: 'Charge', color: '#1d545e' },
];

// Pending designer confirmation — swap these to change which field backs the column.
const SALES_ORDER_FIELD = 'external_order'; // alternative: 'top_level_order'
const PART_FIELD = 'prcpart'; // alternative: 'part' (no PRC prefix)

const PRODUCTION_ORDER_COLUMNS = [
    { label: 'Order', field: 'order_num' },
    { label: 'Sales Order', field: SALES_ORDER_FIELD },
    { label: 'Part', field: PART_FIELD },
    { label: 'Line Status', field: 'line_status', empty: true },
    { label: 'Rev', field: 'rev' },
    { label: 'Line', field: 'line' },
    { label: 'Customer PO', field: 'cust_po' },
    { label: 'Description', field: 'description' },
    { label: 'QTY', field: 'quantity' },
    { label: 'EST Labor Time', field: 'est_labor_time' },
    { label: 'Ship Date', field: 'ship_date' },
];

const DEFAULT_COLUMN_ORDER = PRODUCTION_ORDER_COLUMNS.map((column) => column.field);
const COLUMN_ORDER_STORAGE_KEY = 'productionOrders.columnOrder.v1';
const COLUMN_VISIBILITY_STORAGE_KEY = 'productionOrders.columnVisibility.v1';
const COLUMN_GROUPS_STORAGE_KEY = 'productionOrders.columnGroups.v1';

const NORMAL_GROUP_ID = 'none';
const ORDER_SET_GROUP_ID = 'production-order-set';
const ORDER_SET_MERGED_ID = 'productionorderset';
const ORDER_SET_SOURCE_IDS = ['order_num', SALES_ORDER_FIELD, PART_FIELD, 'cust_po'];
const MAX_CUSTOM_GROUPS = 10;
const GROUPABLE_COLUMN_IDS = DEFAULT_COLUMN_ORDER.filter((field) => field !== 'line_status');

const COLUMN_LABELS = {
    ...Object.fromEntries(PRODUCTION_ORDER_COLUMNS.map((column) => [column.field, column.label])),
    [ORDER_SET_MERGED_ID]: 'Order Set',
};

const isMergedColumnId = (columnId) => (
    columnId === ORDER_SET_MERGED_ID || (typeof columnId === 'string' && columnId.startsWith('group-'))
);

const getMergedIdForGroup = (groupId) => {
    if (groupId === ORDER_SET_GROUP_ID) return ORDER_SET_MERGED_ID;
    if (groupId && groupId.startsWith('custom-')) return `group-${groupId}`;
    return null;
};

const getSourceIdsForGroup = (groupId, customGroups = []) => {
    if (groupId === ORDER_SET_GROUP_ID) return [...ORDER_SET_SOURCE_IDS];
    const custom = customGroups.find((group) => group.id === groupId);
    return (custom?.columnIds || []).filter((id) => GROUPABLE_COLUMN_IDS.includes(id));
};

const sanitizeColumnOrder = (savedOrder, excludeColumnIds = []) => {
    if (!Array.isArray(savedOrder)) return [...DEFAULT_COLUMN_ORDER];
    const excludeSet = new Set(excludeColumnIds);
    const valid = savedOrder.filter(
        (id) => (DEFAULT_COLUMN_ORDER.includes(id) || isMergedColumnId(id)) && !excludeSet.has(id)
    );
    const missing = DEFAULT_COLUMN_ORDER.filter((id) => !valid.includes(id) && !excludeSet.has(id));
    if (!valid.length) return [...DEFAULT_COLUMN_ORDER].filter((id) => !excludeSet.has(id));
    return [...valid, ...missing];
};

const buildEffectiveColumnOrder = (baseColumnOrder, groupId, customGroups = []) => {
    const order = Array.isArray(baseColumnOrder) && baseColumnOrder.length
        ? [...baseColumnOrder]
        : [...DEFAULT_COLUMN_ORDER];
    if (!groupId || groupId === NORMAL_GROUP_ID) {
        return sanitizeColumnOrder(order.filter((id) => !isMergedColumnId(id)));
    }
    const sourceIds = getSourceIdsForGroup(groupId, customGroups);
    const mergedId = getMergedIdForGroup(groupId);
    if (!sourceIds.length || !mergedId) return sanitizeColumnOrder(order);
    const sourceSet = new Set(sourceIds);
    const result = [];
    let mergedInserted = false;
    order.forEach((id) => {
        if (isMergedColumnId(id)) {
            if (id === mergedId && !mergedInserted) {
                result.push(mergedId);
                mergedInserted = true;
            }
            return;
        }
        if (sourceSet.has(id)) {
            if (!mergedInserted) {
                result.push(mergedId);
                mergedInserted = true;
            }
            return;
        }
        if (DEFAULT_COLUMN_ORDER.includes(id)) result.push(id);
    });
    if (!mergedInserted) {
        const insertAt = order.findIndex((id) => sourceSet.has(id));
        result.splice(insertAt >= 0 ? Math.min(insertAt, result.length) : result.length, 0, mergedId);
    }
    DEFAULT_COLUMN_ORDER.forEach((id) => {
        if (!sourceSet.has(id) && !result.includes(id)) result.push(id);
    });
    return result;
};

const expandMergedColumnOrder = (effectiveOrder, mergedId, sourceIds) => {
    if (!mergedId || !Array.isArray(effectiveOrder)) return [...DEFAULT_COLUMN_ORDER];
    const idx = effectiveOrder.indexOf(mergedId);
    if (idx === -1) return effectiveOrder.filter((id) => DEFAULT_COLUMN_ORDER.includes(id));
    const presentSources = sourceIds.filter((id) => DEFAULT_COLUMN_ORDER.includes(id));
    return [
        ...effectiveOrder.slice(0, idx),
        ...presentSources,
        ...effectiveOrder.slice(idx + 1).filter((id) => !isMergedColumnId(id)),
    ];
};

const getEffectiveColumnOrder = (savedOrder, activeGroupId, customGroups = []) => {
    const baseOnly = sanitizeColumnOrder((savedOrder || []).filter((id) => !isMergedColumnId(id)));
    if (!activeGroupId || activeGroupId === NORMAL_GROUP_ID) return baseOnly;
    const sourceIds = getSourceIdsForGroup(activeGroupId, customGroups);
    const mergedId = getMergedIdForGroup(activeGroupId);
    let order = (savedOrder || []).some(isMergedColumnId)
        ? sanitizeColumnOrder(savedOrder, sourceIds)
        : buildEffectiveColumnOrder(baseOnly, activeGroupId, customGroups);
    if (mergedId && !order.includes(mergedId)) {
        order = buildEffectiveColumnOrder(baseOnly, activeGroupId, customGroups);
    }
    return sanitizeColumnOrder(order, sourceIds);
};

const loadColumnGroupsState = () => {
    const fallback = {
        activeGroupId: NORMAL_GROUP_ID,
        defaultGroupId: NORMAL_GROUP_ID,
        customGroups: [],
    };
    try {
        const raw = localStorage.getItem(`${COLUMN_GROUPS_STORAGE_KEY}.${columnStorageUser()}`);
        if (!raw) return fallback;
        const parsed = JSON.parse(raw);
        const customGroups = Array.isArray(parsed.customGroups)
            ? parsed.customGroups.slice(0, MAX_CUSTOM_GROUPS).map((group) => ({
                ...group,
                columnIds: (group.columnIds || []).filter((id) => GROUPABLE_COLUMN_IDS.includes(id)),
            }))
            : [];
        return {
            activeGroupId: parsed.activeGroupId || NORMAL_GROUP_ID,
            defaultGroupId: parsed.defaultGroupId || NORMAL_GROUP_ID,
            customGroups,
        };
    } catch {
        return fallback;
    }
};

const getStartupColumnGroupsState = () => {
    const groups = loadColumnGroupsState();
    const startGroupId = groups.defaultGroupId || NORMAL_GROUP_ID;
    return { ...groups, activeGroupId: startGroupId };
};

const columnStorageUser = () => (localStorage.getItem('userEmail') || 'anonymous').toLowerCase().trim();

const loadProductionColumnOrder = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(`${COLUMN_ORDER_STORAGE_KEY}.${columnStorageUser()}`));
        if (!Array.isArray(saved)) return [...DEFAULT_COLUMN_ORDER];
        const valid = saved.filter((field) => DEFAULT_COLUMN_ORDER.includes(field) || isMergedColumnId(field));
        return valid.length ? valid : [...DEFAULT_COLUMN_ORDER];
    } catch {
        return [...DEFAULT_COLUMN_ORDER];
    }
};

const loadProductionColumnVisibility = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(`${COLUMN_VISIBILITY_STORAGE_KEY}.${columnStorageUser()}`));
        return saved && typeof saved === 'object' && !Array.isArray(saved) ? saved : {};
    } catch {
        return {};
    }
};

const SEARCH_FIELDS = ['order_num', PART_FIELD, 'cust_po', 'description'];
const NUMERIC_FIELDS = new Set(['line', 'quantity']);

// GFSC PART is the SOC-only counterpart of the Orders "Assemblies" toggle (ASY + SOC).
const isGfscPart = (row) => {
    const part = String(row?.[PART_FIELD] ?? row?.part ?? '').trim().toUpperCase();
    return part.startsWith('SOC');
};

const isBlank = (v) => v === null || v === undefined || v === '';

const display = (v) => (isBlank(v) ? '—' : v);

const formatQty = (n) => {
    if (isBlank(n)) return '—';
    const num = Number(n);
    if (Number.isNaN(num)) return n;
    return Number.isInteger(num) ? num : num.toFixed(2);
};

// "HHH:MM:SS" -> seconds; hours can exceed 24 so this is a duration, not a time of day.
const laborSeconds = (v) => {
    const [h = 0, m = 0, s = 0] = String(v).split(':').map(Number);
    return h * 3600 + m * 60 + s;
};

const compareValues = (field, a, b) => {
    if (NUMERIC_FIELDS.has(field)) return Number(a) - Number(b);
    if (field === 'est_labor_time') return laborSeconds(a) - laborSeconds(b);
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

// Same label as Orders getCetecOrderNumber: "ordernum(name)" when both exist.
const salesOrderLabel = (orderNum, row) => {
    const partialName = String(row?.name || row?.order?.name || '').trim();
    if (orderNum && partialName) return `${orderNum}(${partialName})`;
    return orderNum || partialName;
};

const salesOrderNumber = (row) => {
    const value = row?.[SALES_ORDER_FIELD];
    if (isBlank(value)) return '';
    return String(value).trim().replace(/^MN/i, '');
};

const productionRowKey = (row) => String(row?.id ?? `${row?.order_num}-${row?.line}-${row?.[PART_FIELD]}`);

const statusMatchKey = (orderNum, line) => `${String(orderNum ?? '').trim()}|${String(line ?? '').trim()}`;

const parseLineStatus = (raw) => {
    if (isBlank(raw)) return [];
    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!Array.isArray(parsed)) return [];
        return parsed.map((item) => {
            const label = typeof item === 'string' ? item : item?.label;
            if (isBlank(label)) return null;
            const known = LINE_STATUS_TAGS.find((tag) => tag.label.toLowerCase() === String(label).trim().toLowerCase());
            return known || { label: String(label).trim(), color: item?.color || '#6c757d' };
        }).filter(Boolean);
    } catch {
        return [];
    }
};

const renderCell = (field, row) => {
    const value = row[field];
    if (field === SALES_ORDER_FIELD) {
        if (isBlank(value)) return display(value);
        const orderNum = String(value).trim().replace(/^MN/i, '');
        if (!orderNum) return display(value);
        return (
            <a
                href={`https://gigaflightinc.cetecerp.com/order/MN${orderNum}/view`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
            >
                {salesOrderLabel(orderNum, row)}
            </a>
        );
    }
    if (field === 'quantity') return formatQty(value);
    if (field === 'description') {
        return (
            <div
                title={value || ''}
                style={{ maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
                {display(value)}
            </div>
        );
    }
    return display(value);
};

const ProductionOrders = () => {
    const { getProductionLines } = ReceiveContext();
    const { getNewOrders } = OrderContext();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [gfscPartFilter, setGfscPartFilter] = useState(false);
    const [sort, setSort] = useState({ field: '', dir: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(-1);
    const [columnGroupsState, setColumnGroupsState] = useState(getStartupColumnGroupsState);
    const { activeGroupId, defaultGroupId, customGroups } = columnGroupsState;
    const [columnOrder, setColumnOrder] = useState(() => {
        const groups = getStartupColumnGroupsState();
        const saved = loadProductionColumnOrder();
        if (!groups.activeGroupId || groups.activeGroupId === NORMAL_GROUP_ID) {
            let order = saved;
            if (order.includes(ORDER_SET_MERGED_ID)) {
                order = expandMergedColumnOrder(order, ORDER_SET_MERGED_ID, ORDER_SET_SOURCE_IDS);
            }
            groups.customGroups.forEach((group) => {
                const mergedId = getMergedIdForGroup(group.id);
                if (mergedId && order.includes(mergedId)) {
                    order = expandMergedColumnOrder(order, mergedId, group.columnIds || []);
                }
            });
            return sanitizeColumnOrder(order.filter((id) => !isMergedColumnId(id)));
        }
        return getEffectiveColumnOrder(saved, groups.activeGroupId, groups.customGroups);
    });
    const [columnVisibility, setColumnVisibility] = useState(loadProductionColumnVisibility);
    const [draggedColumnField, setDraggedColumnField] = useState(null);
    const [showColumnVisibilityModal, setShowColumnVisibilityModal] = useState(false);
    const [showColumnGroupsModal, setShowColumnGroupsModal] = useState(false);
    const suppressHeaderClickRef = useRef(false);
    const [orderLineByKey, setOrderLineByKey] = useState({});
    const [lineStatusSelections, setLineStatusSelections] = useState({});
    const [submittedLineStatuses, setSubmittedLineStatuses] = useState({});
    const [lineTagsToDeselect, setLineTagsToDeselect] = useState({});
    const [lineStatusMenu, setLineStatusMenu] = useState({ open: false, key: null, top: 0, left: 0, width: 220 });

    const effectiveColumnOrder = useMemo(
        () => getEffectiveColumnOrder(columnOrder, activeGroupId, customGroups),
        [columnOrder, activeGroupId, customGroups]
    );

    const orderedColumns = useMemo(() => {
        const byField = new Map(PRODUCTION_ORDER_COLUMNS.map((column) => [column.field, column]));
        return effectiveColumnOrder.map((field) => {
            if (field === ORDER_SET_MERGED_ID) {
                return { field, label: 'Order Set', merged: true, empty: false, sortField: 'order_num' };
            }
            if (isMergedColumnId(field)) {
                const group = customGroups.find((item) => getMergedIdForGroup(item.id) === field);
                return { field, label: group?.name || 'Group', merged: true, empty: true };
            }
            return byField.get(field) || null;
        }).filter(Boolean);
    }, [effectiveColumnOrder, customGroups]);

    const visibleColumns = useMemo(
        () => orderedColumns.filter((column) => columnVisibility[column.field] !== false),
        [orderedColumns, columnVisibility]
    );

    const hiddenColumnCount = orderedColumns.length - visibleColumns.length;

    useEffect(() => {
        try {
            localStorage.setItem(`${COLUMN_ORDER_STORAGE_KEY}.${columnStorageUser()}`, JSON.stringify(columnOrder));
        } catch { /* ignore storage errors */ }
    }, [columnOrder]);

    useEffect(() => {
        try {
            localStorage.setItem(`${COLUMN_GROUPS_STORAGE_KEY}.${columnStorageUser()}`, JSON.stringify(columnGroupsState));
        } catch { /* ignore storage errors */ }
    }, [columnGroupsState]);

    const handleColumnDragStart = (event, field) => {
        setDraggedColumnField(field);
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            try { event.dataTransfer.setData('text/plain', field); } catch { /* noop */ }
        }
    };

    const handleColumnDrop = (event, targetField) => {
        event.preventDefault();
        const sourceField = draggedColumnField || event.dataTransfer?.getData('text/plain');
        suppressHeaderClickRef.current = true;
        setDraggedColumnField(null);
        if (!sourceField || sourceField === targetField) return;
        setColumnOrder((prev) => {
            const next = [...prev];
            const from = next.indexOf(sourceField);
            const to = next.indexOf(targetField);
            if (from === -1 || to === -1) return prev;
            next.splice(from, 1);
            next.splice(to, 0, sourceField);
            return next;
        });
    };

    const resetColumnOrder = () => {
        const nextOrder = activeGroupId && activeGroupId !== NORMAL_GROUP_ID
            ? buildEffectiveColumnOrder([...DEFAULT_COLUMN_ORDER], activeGroupId, customGroups)
            : [...DEFAULT_COLUMN_ORDER];
        setColumnOrder(nextOrder);
        try { localStorage.removeItem(`${COLUMN_ORDER_STORAGE_KEY}.${columnStorageUser()}`); } catch { /* ignore */ }
    };

    const handleColumnGroupsApply = ({ activeGroupId: nextActiveId, defaultGroupId: nextDefaultId }) => {
        setColumnGroupsState((prev) => {
            setColumnOrder((prevOrder) => {
                const baseOrder = sanitizeColumnOrder(prevOrder.filter((id) => !isMergedColumnId(id)));
                if (!nextActiveId || nextActiveId === NORMAL_GROUP_ID) {
                    const prevMerged = getMergedIdForGroup(prev.activeGroupId);
                    const prevSources = getSourceIdsForGroup(prev.activeGroupId, prev.customGroups);
                    if (prevMerged && prevSources.length) {
                        return expandMergedColumnOrder(prevOrder, prevMerged, prevSources);
                    }
                    return baseOrder;
                }
                return buildEffectiveColumnOrder(baseOrder, nextActiveId, prev.customGroups);
            });
            return {
                ...prev,
                activeGroupId: nextActiveId,
                defaultGroupId: nextDefaultId,
            };
        });
    };

    const handleSaveCustomGroup = (group) => {
        const safeColumnIds = (group.columnIds || []).filter((id) => GROUPABLE_COLUMN_IDS.includes(id));
        setColumnGroupsState((prev) => {
            const payload = { ...group, columnIds: safeColumnIds };
            const existing = prev.customGroups.findIndex((item) => item.id === payload.id);
            const nextGroups = [...prev.customGroups];
            if (existing >= 0) nextGroups[existing] = payload;
            else nextGroups.push(payload);
            return { ...prev, customGroups: nextGroups };
        });
    };

    const handleDeleteCustomGroup = (groupId) => {
        setColumnGroupsState((prev) => ({
            ...prev,
            customGroups: prev.customGroups.filter((group) => group.id !== groupId),
            activeGroupId: prev.activeGroupId === groupId ? NORMAL_GROUP_ID : prev.activeGroupId,
            defaultGroupId: prev.defaultGroupId === groupId ? NORMAL_GROUP_ID : prev.defaultGroupId,
        }));
        if (activeGroupId === groupId) {
            setColumnOrder((prev) => expandMergedColumnOrder(
                prev,
                getMergedIdForGroup(groupId),
                getSourceIdsForGroup(groupId, customGroups)
            ));
        }
    };

    const renderMergedCell = (mergedId, row) => {
        const sourceIds = mergedId === ORDER_SET_MERGED_ID
            ? ORDER_SET_SOURCE_IDS
            : (customGroups.find((group) => getMergedIdForGroup(group.id) === mergedId)?.columnIds || []);
        const lines = sourceIds.map((field) => ({
            label: COLUMN_LABELS[field] || field,
            content: renderCell(field, row),
            show: !isBlank(row?.[field]),
        }));
        return <CombinedColumnCell lines={lines} />;
    };

    const handleColumnVisibilityChange = (nextVisibility) => {
        const next = nextVisibility || {};
        setColumnVisibility(next);
        try {
            localStorage.setItem(`${COLUMN_VISIBILITY_STORAGE_KEY}.${columnStorageUser()}`, JSON.stringify(next));
        } catch { /* ignore storage errors */ }
    };

    const handleShowAllColumns = () => handleColumnVisibilityChange({});

    const handleHeaderClick = (field, empty) => {
        if (suppressHeaderClickRef.current) {
            suppressHeaderClickRef.current = false;
            return;
        }
        if (!empty) handleSort(field);
    };

    const loadLines = useCallback(async () => {
        setLoading(true);
        setError(null);
        const data = await getProductionLines();
        if (data === null) {
            setError(messages.production_orders_load_error);
            setRows([]);
        } else {
            setRows(data);
        }
        setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadLines();
    }, [loadLines]);

    // Line status is stored on the sales order line. Match each production row by sales order + line.
    useEffect(() => {
        const orderNums = [...new Set(rows.map(salesOrderNumber).filter(Boolean))];
        if (orderNums.length === 0) {
            setOrderLineByKey({});
            return undefined;
        }

        let cancelled = false;
        const loadStatuses = async () => {
            const map = {};
            const queue = [...orderNums];
            const pull = async () => {
                while (queue.length > 0 && !cancelled) {
                    const orderNum = queue.shift();
                    const data = await getNewOrders(
                        0, 500, '', '', '', '', '', '', '', '', '', '', '', '', '', true, false, null, orderNum
                    );
                    (data?.order || []).forEach((line) => {
                        map[statusMatchKey(orderNum, line.lineitem)] = {
                            id: line.id,
                            status: parseLineStatus(line.lineItemStatus),
                        };
                    });
                }
            };
            await Promise.all(Array.from({ length: Math.min(4, orderNums.length) }, pull));
            if (cancelled) return;
            setOrderLineByKey(map);
            setSubmittedLineStatuses((prev) => {
                const next = { ...prev };
                rows.forEach((row) => {
                    const key = productionRowKey(row);
                    const match = map[statusMatchKey(salesOrderNumber(row), row.line)];
                    if (match && !(next[key] || []).length && !(lineStatusSelections[key] || []).length) {
                        next[key] = match.status;
                    }
                });
                return next;
            });
        };
        loadStatuses();
        return () => {
            cancelled = true;
        };
    // getNewOrders is recreated each render; reload only when the line list changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    const closeLineStatusMenu = useCallback(() => {
        setLineStatusMenu({ open: false, key: null, top: 0, left: 0, width: 220 });
    }, []);

    useEffect(() => {
        if (!lineStatusMenu.open) return undefined;
        const close = (event) => {
            const target = event?.target;
            if (target?.closest?.('.production-line-status-trigger') || target?.closest?.('.production-line-status-menu')) {
                return;
            }
            closeLineStatusMenu();
        };
        document.addEventListener('mousedown', close);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('mousedown', close);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [lineStatusMenu.open, closeLineStatusMenu]);

    const openLineStatusMenu = (row, event) => {
        const key = productionRowKey(row);
        if (lineStatusMenu.open && lineStatusMenu.key === key) {
            closeLineStatusMenu();
            return;
        }
        const rect = event.currentTarget.getBoundingClientRect();
        const menuHeight = 220;
        const width = Math.max(rect.width, 180);
        const spaceBelow = window.innerHeight - rect.bottom;
        const top = spaceBelow < menuHeight && rect.top > spaceBelow
            ? Math.max(8, rect.top - menuHeight)
            : rect.bottom + 2;
        const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
        setLineStatusMenu({ open: true, key, top, left, width });
    };

    const toggleLineStatusTag = (row, tag) => {
        const key = productionRowKey(row);
        const selected = lineStatusSelections[key] || [];
        const submitted = submittedLineStatuses[key] || [];
        const marked = lineTagsToDeselect[key] || [];
        const inSelection = selected.some((item) => item.label === tag.label);
        const inSubmitted = submitted.some((item) => item.label === tag.label);
        const isMarked = marked.some((item) => item.label === tag.label);
        const isOn = inSelection || (inSubmitted && !isMarked);

        if (isOn) {
            if (inSelection) {
                setLineStatusSelections((prev) => ({
                    ...prev,
                    [key]: (prev[key] || []).filter((item) => item.label !== tag.label),
                }));
            }
            if (inSubmitted && !isMarked) {
                setLineTagsToDeselect((prev) => ({ ...prev, [key]: [...(prev[key] || []), tag] }));
            }
            return;
        }

        if (isMarked) {
            setLineTagsToDeselect((prev) => ({
                ...prev,
                [key]: (prev[key] || []).filter((item) => item.label !== tag.label),
            }));
        }
        if (!inSubmitted && !inSelection) {
            setLineStatusSelections((prev) => ({ ...prev, [key]: [...(prev[key] || []), tag] }));
        }
    };

    const hasLineStatusChanges = (key) => (
        (lineStatusSelections[key] || []).length > 0 || (lineTagsToDeselect[key] || []).length > 0
    );

    const submitLineStatus = async (row) => {
        const key = productionRowKey(row);
        const orderLine = orderLineByKey[statusMatchKey(salesOrderNumber(row), row.line)];
        if (!orderLine?.id) {
            alert('This production line is not linked to a sales order line, so the line status cannot be saved.');
            return;
        }
        const selected = lineStatusSelections[key] || [];
        const submitted = submittedLineStatuses[key] || [];
        const removing = lineTagsToDeselect[key] || [];
        const finalSelections = [
            ...selected,
            ...submitted.filter((tag) => !removing.some((item) => item.label === tag.label)),
        ].filter((tag, index, list) => list.findIndex((item) => item.label === tag.label) === index);

        const result = await saveLineStatus({
            orderLineId: orderLine.id,
            status: JSON.stringify(finalSelections),
        });
        if (!result) {
            alert('Failed to save line status. Please try again.');
            return;
        }
        setSubmittedLineStatuses((prev) => ({ ...prev, [key]: finalSelections }));
        setLineStatusSelections((prev) => ({ ...prev, [key]: [] }));
        setLineTagsToDeselect((prev) => ({ ...prev, [key]: [] }));
        closeLineStatusMenu();
    };

    // Cycles asc -> desc -> default (server order: order_num, line).
    const handleSort = (field) => {
        setSort((prev) => {
            if (prev.field !== field) return { field, dir: 'asc' };
            if (prev.dir === 'asc') return { field, dir: 'desc' };
            return { field: '', dir: 'asc' };
        });
    };

    const visibleRows = useMemo(() => {
        const term = search.trim().toLowerCase();
        let result = rows;
        if (gfscPartFilter) {
            result = result.filter(isGfscPart);
        }
        if (term) {
            result = result.filter((row) =>
                SEARCH_FIELDS.some((f) => !isBlank(row[f]) && String(row[f]).toLowerCase().includes(term))
            );
        }

        if (sort.field) {
            const dir = sort.dir === 'asc' ? 1 : -1;
            result = [...result].sort((a, b) => {
                const va = a[sort.field];
                const vb = b[sort.field];
                // Nulls always sort last regardless of direction
                if (isBlank(va) && isBlank(vb)) return 0;
                if (isBlank(va)) return 1;
                if (isBlank(vb)) return -1;
                return compareValues(sort.field, va, vb) * dir;
            });
        }
        return result;
    }, [rows, search, gfscPartFilter, sort]);

    const totalRecords = visibleRows.length;
    const totalPages = itemsPerPage === -1
        ? 1
        : Math.max(1, Math.ceil(totalRecords / itemsPerPage) || 1);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const pageRows = useMemo(() => {
        if (itemsPerPage === -1) return visibleRows;
        const start = (currentPage - 1) * itemsPerPage;
        return visibleRows.slice(start, start + itemsPerPage);
    }, [visibleRows, currentPage, itemsPerPage]);

    const hasPaginatedRecords = pageRows.length > 0;
    const startRecord = hasPaginatedRecords
        ? (itemsPerPage === -1 ? 1 : (currentPage - 1) * itemsPerPage + 1)
        : 0;
    const endRecord = hasPaginatedRecords ? startRecord + pageRows.length - 1 : 0;

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const SortIcon = ({ field }) => {
        if (sort.field !== field) return <i className="fa fa-sort text-muted ms-1" style={{ fontSize: '11px' }} />;
        return sort.dir === 'asc' ? <i className="fa fa-sort-up ms-1" /> : <i className="fa fa-sort-down ms-1" />;
    };

    const colSpan = Math.max(visibleColumns.length, 1);

    const renderLineStatusCell = (row) => {
        const key = productionRowKey(row);
        const selected = lineStatusSelections[key] || [];
        const submitted = submittedLineStatuses[key] || [];
        const removing = lineTagsToDeselect[key] || [];
        const isOpen = lineStatusMenu.open && lineStatusMenu.key === key;

        return (
            <div className="d-flex align-items-center gap-1">
                <div
                    className="form-control d-flex align-items-center justify-content-between flex-grow-1 production-line-status-trigger"
                    style={{
                        cursor: 'pointer',
                        minHeight: '38px',
                        backgroundColor: 'white',
                        border: '1px solid #ced4da',
                        minWidth: '150px',
                    }}
                    onClick={(event) => openLineStatusMenu(row, event)}
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                >
                    <div className="d-flex flex-wrap gap-1">
                        {selected.map((tag) => (
                            <span
                                key={`new-${tag.label}`}
                                className="badge"
                                style={{
                                    backgroundColor: tag.color,
                                    color: 'white',
                                    fontSize: '0.75rem',
                                    padding: '4px 8px',
                                    borderRadius: '12px',
                                }}
                            >
                                {tag.label}
                            </span>
                        ))}
                        {submitted.map((tag) => {
                            const isMarked = removing.some((item) => item.label === tag.label);
                            return (
                                <span
                                    key={`saved-${tag.label}`}
                                    className="badge"
                                    style={{
                                        backgroundColor: isMarked ? '#6c757d' : tag.color,
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        padding: '4px 8px',
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        opacity: isMarked ? 0.6 : 1,
                                    }}
                                    title={isMarked ? 'Click to keep this tag' : 'Click to deselect this tag'}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        toggleLineStatusTag(row, tag);
                                    }}
                                >
                                    {tag.label}
                                </span>
                            );
                        })}
                        {selected.length === 0 && submitted.length === 0 && (
                            <span className="text-muted">Select tags...</span>
                        )}
                    </div>
                </div>
                {hasLineStatusChanges(key) && (
                    <button
                        type="button"
                        className="btn btn-success btn-sm"
                        style={{
                            minWidth: '20px',
                            height: '20px',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            borderRadius: '2px',
                            flexShrink: 0,
                        }}
                        title="Submit changes"
                        onClick={(event) => {
                            event.stopPropagation();
                            submitLineStatus(row);
                        }}
                    >
                        ✓
                    </button>
                )}
            </div>
        );
    };

    const renderBody = () => {
        if (loading) {
            return (
                <tr>
                    <td colSpan={colSpan} className="text-center py-5">
                        <div className="spinner-border text-secondary spinner-border-sm me-2" role="status" />
                        <span className="text-muted">Loading production orders...</span>
                    </td>
                </tr>
            );
        }
        if (error) {
            return (
                <tr>
                    <td colSpan={colSpan} className="p-2">
                        <div className="alert alert-danger py-2 mb-0 d-flex align-items-center gap-2">
                            <i className="fa fa-exclamation-triangle" />
                            {error}
                            <button className="btn btn-sm btn-outline-danger ms-auto" onClick={loadLines}>Retry</button>
                        </div>
                    </td>
                </tr>
            );
        }
        if (visibleRows.length === 0) {
            return (
                <tr>
                    <td colSpan={colSpan} className="text-center text-muted py-5">
                        {rows.length > 0
                            ? 'No production orders match the current filters.'
                            : messages.no_production_orders_label}
                    </td>
                </tr>
            );
        }
        return pageRows.map((row) => (
            <tr key={row.id}>
                {visibleColumns.map((column) => (
                    <td
                        key={column.field}
                        className={column.merged ? 'production-col-group align-middle' : (column.field === 'description' ? 'align-middle' : 'text-center align-middle')}
                        style={column.field === 'line_status' ? { minWidth: '190px', width: '190px' } : undefined}
                    >
                        {column.merged
                            ? renderMergedCell(column.field, row)
                            : (column.field === 'line_status' ? renderLineStatusCell(row) : renderCell(column.field, row))}
                    </td>
                ))}
            </tr>
        ));
    };

    return (
        <div className="order-validation-outer">
            <div className="order-validation-page-title-wrap px-3 pt-3 pb-1">
                <h1 className="orders-dashboard-page-title table-section-text table-section-text-left mb-0">
                    {messages.production_orders_label}
                </h1>
            </div>
            <div className="orders-toolbar-date-row orders-dashboard-controls-row production-orders-toolbar px-3 pt-2 pb-2 mb-0">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                    <label className="d-flex align-items-center gap-2 mb-0 small text-muted orders-dashboard-controls-left">
                        <span>Rows</span>
                        <select
                            className="px-2 py-1 border rounded-1 form-select form-select-sm orders-dashboard-page-size"
                            style={{ width: 'auto', minWidth: '72px' }}
                            value={itemsPerPage}
                            onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                            aria-label="Rows per page"
                        >
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                            <option value="-1">All</option>
                        </select>
                    </label>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <button
                            type="button"
                            className="btn btn-sm reset-column-order-btn"
                            onClick={resetColumnOrder}
                            title="Reset column order to default"
                        >
                            <i className="fa fa-undo" style={{ marginRight: '4px' }}></i>
                            Reset Column Order
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm reset-column-order-btn"
                            onClick={() => setShowColumnGroupsModal(true)}
                            title="Combine columns into groups"
                        >
                            <i className="fa fa-table-columns" style={{ marginRight: '4px' }}></i>
                            Column Groups
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm reset-column-order-btn"
                            onClick={() => setShowColumnVisibilityModal(true)}
                            title="Show or hide columns"
                        >
                            <i className="fa fa-columns" style={{ marginRight: '4px' }}></i>
                            Show/Hide Columns
                        </button>
                        {hiddenColumnCount > 0 && (
                            <button
                                type="button"
                                className="btn btn-sm reset-column-order-btn"
                                onClick={handleShowAllColumns}
                                title="Show all hidden columns"
                            >
                                <i className="fa fa-eye" style={{ marginRight: '4px' }}></i>
                                Show all columns ({hiddenColumnCount})
                            </button>
                        )}
                    </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-shrink-0 justify-content-end">
                        <button
                            type="button"
                            className={`btn production-orders-gfsc-btn ${gfscPartFilter ? 'btn-primary' : 'btn-outline-primary'} date-filter-btn assemblies ${gfscPartFilter ? 'active' : ''}`}
                            onClick={() => {
                                setGfscPartFilter((on) => !on);
                                setCurrentPage(1);
                            }}
                            aria-pressed={gfscPartFilter}
                            title="Show parts that start with SOC"
                        >
                            GFSC PART
                        </button>
                        <div className="input-group input-group-sm" style={{ width: '320px' }}>
                            <span className="input-group-text bg-white">
                                <i className="fa fa-search text-muted" />
                            </span>
                            <input
                                type="text"
                                className="form-control border-start-0"
                                placeholder="Search order, part, customer PO, description..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            {search && (
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => {
                                        setSearch('');
                                        setCurrentPage(1);
                                    }}
                                >
                                    <i className="fa fa-times" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="box-body border b-white rounded-2 production-orders-page-shell">
                <div className="production-orders-table-scroll">
                    <table className="table gf-table table-bordered table-hover mb-0 production-orders-table compact-data-table">
                        <thead className="sticky-top bg-light">
                            <tr>
                                {visibleColumns.map((column) => (
                                    <th
                                        key={column.field}
                                        className={column.merged ? 'text-center production-col-group' : 'text-center'}
                                        draggable
                                        onDragStart={(event) => handleColumnDragStart(event, column.field)}
                                        onDragOver={(event) => {
                                            event.preventDefault();
                                            if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
                                        }}
                                        onDrop={(event) => handleColumnDrop(event, column.field)}
                                        onDragEnd={() => setDraggedColumnField(null)}
                                        style={{
                                            whiteSpace: 'nowrap',
                                            cursor: 'grab',
                                            userSelect: 'none',
                                            opacity: draggedColumnField === column.field ? 0.45 : 1,
                                            minWidth: column.field === 'line_status' ? '190px' : undefined,
                                            width: column.field === 'line_status' ? '190px' : undefined,
                                        }}
                                        onClick={() => handleHeaderClick(column.sortField || column.field, column.empty)}
                                        title="Drag to reorder"
                                    >
                                        {column.label}
                                        {!column.empty && <SortIcon field={column.sortField || column.field} />}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>{renderBody()}</tbody>
                    </table>
                </div>
                {!loading && (
                    <nav
                        aria-label="Production Orders pagination"
                        className="production-orders-pagination d-flex justify-content-end align-items-center"
                    >
                        <span className="mb-0">
                            Showing {startRecord} to {endRecord} of {totalRecords}
                        </span>
                        <ul className="pagination d-flex align-items-center mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                    type="button"
                                    className="page-link"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                            </li>
                            <li className="page-item">
                                <select
                                    className="form-select mx-2"
                                    style={{ width: 'auto' }}
                                    value={currentPage}
                                    onChange={(e) => handlePageChange(Number(e.target.value))}
                                    aria-label="Page"
                                >
                                    {[...Array(totalPages)].map((_, index) => (
                                        <option key={index} value={index + 1}>
                                            Page {index + 1}
                                        </option>
                                    ))}
                                </select>
                            </li>
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button
                                    type="button"
                                    className="page-link"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </li>
                        </ul>
                    </nav>
                )}
            </div>
            {lineStatusMenu.open && createPortal(
                <div
                    className="production-line-status-menu"
                    style={{
                        position: 'fixed',
                        top: `${lineStatusMenu.top}px`,
                        left: `${lineStatusMenu.left}px`,
                        width: `${lineStatusMenu.width}px`,
                        zIndex: 1000005,
                        backgroundColor: 'white',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        maxHeight: '220px',
                        overflowY: 'auto',
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                >
                    {LINE_STATUS_TAGS.map((tag) => {
                        const key = lineStatusMenu.key;
                        const selected = (lineStatusSelections[key] || []).some((item) => item.label === tag.label);
                        const submitted = (submittedLineStatuses[key] || []).some((item) => item.label === tag.label);
                        const marked = (lineTagsToDeselect[key] || []).some((item) => item.label === tag.label);
                        const isSelected = selected || (submitted && !marked);
                        const row = rows.find((item) => productionRowKey(item) === key);
                        return (
                            <div
                                key={tag.label}
                                className="p-2 d-flex align-items-center"
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? '#f8f9fa' : 'white',
                                    borderBottom: '1px solid #f1f1f1',
                                }}
                                onMouseEnter={(event) => {
                                    if (!isSelected) event.currentTarget.style.backgroundColor = '#f8f9fa';
                                }}
                                onMouseLeave={(event) => {
                                    event.currentTarget.style.backgroundColor = isSelected ? '#f8f9fa' : 'white';
                                }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    if (row) toggleLineStatusTag(row, tag);
                                }}
                            >
                                <div
                                    className="me-2"
                                    style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '2px',
                                        backgroundColor: tag.color,
                                        border: '1px solid rgba(0,0,0,0.2)',
                                        flexShrink: 0,
                                    }}
                                />
                                <span style={{ fontSize: '0.875rem' }}>{tag.label}</span>
                                {isSelected && <span className="ms-auto">✓</span>}
                            </div>
                        );
                    })}
                </div>,
                document.body
            )}
            <ColumnGroupsModal
                show={showColumnGroupsModal}
                onHide={() => setShowColumnGroupsModal(false)}
                activeGroupId={activeGroupId}
                defaultGroupId={defaultGroupId}
                customGroups={customGroups}
                onApply={handleColumnGroupsApply}
                onSaveCustomGroup={handleSaveCustomGroup}
                onDeleteCustomGroup={handleDeleteCustomGroup}
                groupableColumnIds={GROUPABLE_COLUMN_IDS}
                columnLabels={COLUMN_LABELS}
                builtinGroups={[{ id: ORDER_SET_GROUP_ID, name: 'Order Set' }]}
            />
            <ColumnVisibilityModal
                show={showColumnVisibilityModal}
                onHide={() => setShowColumnVisibilityModal(false)}
                columns={orderedColumns.map((column) => ({ key: column.field, label: column.label }))}
                visibleColumns={columnVisibility}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                pageName="Production Orders"
            />
        </div>
    );
};

export default ProductionOrders;
